<template>
  <div
    v-if="extensions.length"
    class="flex flex-col gap-3"
  >
    <slot name="header">
      <ExtensionGridTitle>{{ title }}</ExtensionGridTitle>
      <slot name="description" />
    </slot>
    <ul
      role="list"
      class="flex flex-row justify-start items-start flex-wrap gap-x-6 gap-y-6"
    >
      <li
        v-for="extension in extensions"
        :key="extension.id"
        class="cursor-pointer"
        @click="$emit('extensionClick', extension)"
      >
        <ExtensionGridItem :extension="extension" :enable-toggle="displayToggle(extension)" />
      </li>
      <li v-if="appendsIcon">
        <router-link
          :to="{name: 'extension-store'}"
          class="w-12 h-12 bg-gray-100 border-[1.5px] border-gray-400 hover:opacity-75 flex items-center rounded-md"
        >
          <Plus class="w-full text-gray-400" :stroke-width="2" />
        </router-link>
      </li>
      <li v-if="$slots['appends']" class="flex flex-col">
        <slot name="appends" />
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import Extension from "@/core/interfaces/extension/Extension";
import {Plus} from "@lucide/vue";
import ExtensionGridTitle from "@/extension/components/extension-grid/ExtensionGridTitle.vue";
import ExtensionGridItem from "@/extension/components/extension-grid/ExtensionGridItem.vue";
import {useSubscriptionPlanStore} from "@/stores/subscription-plan.store";
import {storeToRefs} from "pinia";
import {computed} from "vue";
import SubscriptionPlanService from "@/subscription-plan/services/SubscriptionPlanService";

defineEmits<{
    (e: 'extensionClick', extension: Extension): void;
}>();
const {appendsIcon = false, enableToggle = false} = defineProps<{
    title: string;
    extensions: Extension[];
    appendsIcon?: boolean;
    enableToggle?: boolean;
}>();
const {globalPlan} = storeToRefs(useSubscriptionPlanStore());

/**
 * Determine if the user has an active subscription.
 */
const hasActiveSubscription = computed(() => {
    if (!globalPlan.value) {
        return false;
    }

    const latestTrial = globalPlan.value.latest_trial;

    return SubscriptionPlanService.hasActiveSubscription(globalPlan.value) || latestTrial && latestTrial.status === 'active';
});

const displayToggle = (extension: Extension) => {
    if (!enableToggle) {
        return false;
    }

    if (hasActiveSubscription.value) {
        return true;
    }

    return extension.latest_active_trial?.status === 'active';
};

</script>
